/**
 * Notification Bar Preview Component
 * 
 * Renders a live preview of the notification bar with sortable components
 * 
 * @package TopBuddy
 */

import { useEffect, useState, useRef } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';
import { __ } from '@wordpress/i18n';
import { Button, Modal } from '@wordpress/components';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import TemplateLibrary from './TemplateLibrary';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { updateCSSVariables } from '../utils/cssVariablesUpdater';
import SortablePreviewItem from './SortablePreviewItem';
import AddComponentButton from '../../post-settings/components/sortable/AddComponentButton';
import RotatingText from './RotatingText';

const NotificationBarPreview = () => {
	// Ref for the notification bar element
	const notificationBarRef = useRef(null);

	// Get initial data from window
	const previewData = window.topbuddyPreview || {};
	const postSettingsData = window.topbuddyPostSettings || {};
	const icons = postSettingsData.icons || {};
	const isProActive = previewData.isProActive || postSettingsData.isProActive || false;

	// Get dispatch for UI and components stores
	const { setExpandedPreviewComponent, clearExpandedPreviewComponent, openSettingsTab } = useDispatch('topbuddy/ui');
	const { setActiveComponents: setStoreComponents } = useDispatch('topbuddy/components');

	// Drag state
	const [activeId, setActiveId] = useState(null);

	// Modal state
	const [isModalOpen, setIsModalOpen] = useState(false);

	// Setup drag sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	// Get components and settings from store
	const { activeComponents, availableComponents, settings, expandedPreviewComponentId } = useSelect((select) => {
		const componentsStore = select('topbuddy/components');
		const settingsStore = select('topbuddy/settings');
		const uiStore = select('topbuddy/ui');
		
		return {
			activeComponents: componentsStore?.getActiveComponents() || previewData.initialComponents || [],
			availableComponents: componentsStore?.getAvailableComponents() || previewData.availableComponents || {},
			settings: settingsStore?.getSettings() || {},
			expandedPreviewComponentId: uiStore?.getExpandedPreviewComponentId() || null,
		};
	}, []);

	// Update CSS variables whenever components or settings change
	useEffect(() => {
		updateCSSVariables(settings, activeComponents, notificationBarRef);
	}, [settings, activeComponents]);

	// Reinitialize countdown when countdown component changes
	useEffect(() => {
		const countdownComponent = activeComponents.find(comp => comp.id === 'countdown');
		
		if (countdownComponent && countdownComponent.settings.target_date) {
			// Small delay to ensure DOM is updated
			setTimeout(() => {
				const countdownElement = document.querySelector('.topbuddy-notification-bar-countdown');
				
				if (countdownElement && window.TopBuddy && window.TopBuddy.reinitCountdown) {
					window.TopBuddy.reinitCountdown();
				}
			}, 50);
		}
	}, [activeComponents]);

	// Initialize attention seeker animations when button component changes
	useEffect(() => {
		const buttonComponent = activeComponents.find(comp => comp.id === 'button');
		
		if (buttonComponent && buttonComponent.settings.attention_seeker && buttonComponent.settings.attention_seeker !== 'none') {
			// Small delay to ensure DOM is updated
			setTimeout(() => {
				const buttons = document.querySelectorAll('.topbuddy-notification-bar-button[class*="topbuddy-attention-seeker--"]');
				
				if (buttons.length > 0) {
					// Clear any existing intervals
					buttons.forEach(button => {
						if (button._attentionSeekerInterval) {
							clearInterval(button._attentionSeekerInterval);
							delete button._attentionSeekerInterval;
						}
						if (button._attentionSeekerTimeout) {
							clearTimeout(button._attentionSeekerTimeout);
							delete button._attentionSeekerTimeout;
						}
					});

					// Check if user prefers reduced motion
					const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
					
					if (!prefersReducedMotion) {
						// Get the interval from component settings or default to 15 seconds
						const attentionInterval = (buttonComponent?.settings?.attention_seeker_interval || 15) * 1000;

						buttons.forEach(button => {
							const triggerAnimation = () => {
								button.classList.add('is-animating');
								setTimeout(() => {
									button.classList.remove('is-animating');
								}, 500);
							};

							// First animation after 2 seconds
							button._attentionSeekerTimeout = setTimeout(triggerAnimation, 2000);

							// Then at the specified interval
							button._attentionSeekerInterval = setInterval(triggerAnimation, attentionInterval);
						});
					}
				}
			}, 50);
		}

		// Cleanup function
		return () => {
			const buttons = document.querySelectorAll('.topbuddy-notification-bar-button[class*="topbuddy-attention-seeker--"]');
			buttons.forEach(button => {
				if (button._attentionSeekerInterval) {
					clearInterval(button._attentionSeekerInterval);
					delete button._attentionSeekerInterval;
				}
				if (button._attentionSeekerTimeout) {
					clearTimeout(button._attentionSeekerTimeout);
					delete button._attentionSeekerTimeout;
				}
			});
		};
	}, [activeComponents]);

	// Get available components to add (not in active list)
	const getAvailableToAdd = () => {
		const activeIds = activeComponents.map(comp => comp.id);
		const available = [];

		Object.keys(availableComponents).forEach(componentId => {
			const component = availableComponents[componentId];
			
			// Skip if already active
			if (activeIds.includes(componentId)) {
				return;
			}

			available.push({
				id: componentId,
				...component
			});
		});

		return available;
	};

	// Handle adding a component
	const handleAddComponent = (componentId) => {
		const component = availableComponents[componentId];
		if (!component) {
			return;
		}

		// Get default settings for this component
		const defaultSettings = {};
		if (component.settings) {
			Object.keys(component.settings).forEach(key => {
				const setting = component.settings[key];
				if (setting.default !== undefined) {
					defaultSettings[key] = setting.default;
				}
			});
		}

		setStoreComponents([
			...activeComponents,
			{
				id: componentId,
				settings: defaultSettings
			}
		]);
	};

	// Handle drag start
	const handleDragStart = (event) => {
		setActiveId(event.active.id);
	};

	// Handle drag end
	const handleDragEnd = (event) => {
		const { active, over } = event;

		if (!over || active.id === over.id) {
			setActiveId(null);
			return;
		}

		const oldIndex = activeComponents.findIndex((item) => item.id === active.id);
		const newIndex = activeComponents.findIndex((item) => item.id === over.id);

		if (oldIndex !== -1 && newIndex !== -1) {
			const newComponents = arrayMove(activeComponents, oldIndex, newIndex);
			setStoreComponents(newComponents);
		}

		setActiveId(null);
	};

	// Handle drag cancel
	const handleDragCancel = () => {
		setActiveId(null);
	};

	// Handle component click to toggle settings
	const handleComponentClick = (e, componentId) => {
		if (e && e.stopPropagation) {
			e.stopPropagation();
		}
		if (expandedPreviewComponentId === componentId) {
			clearExpandedPreviewComponent();
		} else {
			setExpandedPreviewComponent(componentId);
		}
	};

	// Handle settings update
	const handleUpdateSettings = (componentId, newSettings) => {
		const updatedComponents = activeComponents.map(comp =>
			comp.id === componentId ? { ...comp, settings: newSettings } : comp
		);
		setStoreComponents(updatedComponents);
	};

	// Handle component removal
	const handleRemoveComponent = (componentId) => {
		const updatedComponents = activeComponents.filter(comp => comp.id !== componentId);
		setStoreComponents(updatedComponents);
		if (expandedPreviewComponentId === componentId) {
			clearExpandedPreviewComponent();
		}
	};

	// Handle bar click (clicking on empty space)
	const handleBarClick = (e) => {
		// Check if we're clicking on the bar itself or container (not on a component)
		const isBarOrContainer = 
			e.target.classList.contains('topbuddy-notification-bar') ||
			e.target.classList.contains('topbuddy-notification-bar-container');
		
		if (isBarOrContainer) {
			openSettingsTab();
		}
	};

	// Render a component
	const renderComponent = (component) => {
		const config = availableComponents[component.id];
		if (!config) {
			return null;
		}

		const componentSettings = component.settings || {};
		const isExpanded = expandedPreviewComponentId === component.id;

		let componentElement = null;

		switch (component.id) {
			case 'title':
				const TitleTag = componentSettings.tag || 'h3';
				const titleClass = componentSettings.grow 
					? 'topbuddy-notification-bar-title topbuddy-notification-bar-grow'
					: 'topbuddy-notification-bar-title';
				componentElement = (
					<TitleTag 
						className={titleClass}
						style={{ cursor: 'pointer' }}
					>
						{componentSettings.text || __('Welcome', 'topbuddy')}
					</TitleTag>
				);
				break;

			case 'message':
				const messageClass = componentSettings.grow 
					? 'topbuddy-notification-bar-message topbuddy-notification-bar-grow'
					: 'topbuddy-notification-bar-message';
				componentElement = (
					<div 
						className={messageClass}
						style={{ cursor: 'pointer' }}
						dangerouslySetInnerHTML={{ __html: componentSettings.text || '' }}
					/>
				);
				break;

			case 'button':
				const buttonStyle = componentSettings.button_style || 'button';
				const attentionSeeker = componentSettings.attention_seeker || 'none';
				const buttonClasses = `topbuddy-notification-bar-button${attentionSeeker !== 'none' ? ` topbuddy-attention-seeker--${attentionSeeker}` : ''}`;
				componentElement = (
					<a
						href={componentSettings.url || '#'}
						className={buttonClasses}
						data-style={buttonStyle}
						target={componentSettings.target ? '_blank' : '_self'}
						rel={componentSettings.target ? 'noopener noreferrer' : ''}
						onClick={(e) => e.preventDefault()}
						style={{ cursor: 'pointer' }}
					>
						{componentSettings.text || __('Click Here', 'topbuddy')}
					</a>
				);
				break;

			case 'icon':
				const iconName = componentSettings.icon_name || 'check';
				const iconHtml = icons[iconName] || '';
				
				componentElement = (
					<div
						className="topbuddy-notification-bar-icon"
						style={{ cursor: 'pointer' }}
						dangerouslySetInnerHTML={{ __html: iconHtml }}
					/>
				);
				break;

			case 'image':
				const imageUrl = componentSettings.image_url || '';
				const altText = componentSettings.alt_text || '';
				const imageSize = componentSettings.size || 'medium';
				
				// Map size to CSS variable
				const imageSizeVar = `var(--topbuddy-notification-bar-image-size-${imageSize})`;
				
				componentElement = imageUrl ? (
					<img
						src={imageUrl}
						alt={altText}
						className="topbuddy-notification-bar-image"
						style={{ cursor: 'pointer', height: imageSizeVar }}
					/>
				) : (
					<div
						className="topbuddy-notification-bar-image"
						style={{ 
							cursor: 'pointer',
							height: imageSizeVar,
							background: '#ddd',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: '#666',
							fontSize: '12px',
							padding: '10px'
						}}
					>
						{__('No Image', 'topbuddy')}
					</div>
				);
				break;

			case 'shortcode':
				componentElement = (
					<div 
						className="topbuddy-notification-bar-shortcode"
						style={{ 
							cursor: 'pointer',
							padding: '8px 12px',
							background: 'rgba(255, 255, 255, 0.1)',
							borderRadius: '4px',
							fontSize: '13px',
							color: 'rgba(255, 255, 255, 0.5)',
						}}
					>
						<span className="dashicons dashicons-shortcode" style={{ fontSize: '16px', marginRight: '4px' }}></span>
						{__('Preview on frontend only', 'topbuddy')}
					</div>
				);
				break;

			case 'elementor':
				componentElement = (
					<div 
						className="topbuddy-notification-bar-elementor"
						style={{ 
							cursor: 'pointer',
							padding: '8px 12px',
							background: 'rgba(255, 255, 255, 0.1)',
							borderRadius: '4px',
							fontSize: '13px',
							color: 'rgba(255, 255, 255, 0.5)',
						}}
					>
						<span className="dashicons dashicons-admin-page" style={{ fontSize: '16px', marginRight: '4px' }}></span>
						{__('Preview on frontend only', 'topbuddy')}
					</div>
				);
				break;

			case 'html':
				const htmlCode = componentSettings.html_code || '';
				componentElement = htmlCode ? (
					<div 
						className="topbuddy-notification-bar-html"
						style={{ cursor: 'pointer' }}
						dangerouslySetInnerHTML={{ __html: htmlCode }}
					/>
				) : (
					<div 
						className="topbuddy-notification-bar-html"
						style={{ 
							cursor: 'pointer',
							padding: '8px 12px',
							background: 'rgba(255, 255, 255, 0.1)',
							borderRadius: '4px',
							fontSize: '13px',
							color: 'rgba(255, 255, 255, 0.5)',
						}}
					>
						<span className="dashicons dashicons-editor-code" style={{ fontSize: '16px', marginRight: '4px' }}></span>
						{__('Add HTML code', 'topbuddy')}
					</div>
				);
				break;

			case 'coupon':
				const couponCode = componentSettings.code || 'SAVE20';
				const couponSize = componentSettings.size || 'medium';
				const couponBgColor = componentSettings.background_color || '#000000';
				const couponTextColor = componentSettings.text_color || '#ffffff';
				const couponBorderColor = componentSettings.border_color || '#ffffff';
				
				componentElement = (
					<div 
						className={`topbuddy-notification-bar-coupon topbuddy-notification-bar-coupon--${couponSize}`}
						style={{
							backgroundColor: couponBgColor,
							color: couponTextColor,
							borderColor: couponBorderColor,
							cursor: 'pointer'
						}}
					>
						<span className="topbuddy-notification-bar-coupon__code">{couponCode}</span>
					</div>
				);
				break;

			case 'rotating_text':
				const rawMessages = String(componentSettings.messages || '');
				// Split by asterisks to match PHP rendering.
				const messageItems = rawMessages
					.split('*')
					.map((msg) => msg.trim())
					.filter((msg) => msg.length > 0);

				const speed = componentSettings.speed || 3000;
				const transitionType = componentSettings.transition_type || 'fade';

				// Use default message if no messages provided
				const messagesToShow = messageItems.length > 0 
					? messageItems 
					: [__('Rotating text', 'topbuddy')];

				componentElement = (
					<RotatingText 
						messages={messagesToShow}
						speed={speed}
						transitionType={transitionType}
					/>
				);
				break;

			case 'countdown':
				const targetDate = componentSettings.target_date || '';
				const countdownTheme = componentSettings.theme || 'simple';
				const labelsPlural = componentSettings.labels_plural || __('Days,Hours,Minutes,Seconds', 'topbuddy');
				const labelsSingular = componentSettings.labels_singular || __('Day,Hour,Minute,Second', 'topbuddy');
				const onComplete = componentSettings.on_complete || 'nothing';
				const messageText = componentSettings.message_text || __('The offer has ended!', 'topbuddy');
			const countdownType = componentSettings.countdown_type || 'fixed';
			const repeatInterval = parseInt(componentSettings.repeat_interval) || 0;
			
			// Calculate timestamp based on countdown type
			let calculatedTimestamp = 0;
			if (countdownType === 'repeating' && repeatInterval > 0) {
				// For repeating countdowns, calculate next interval from Unix epoch
				const intervalSeconds = repeatInterval * 3600; // Convert hours to seconds
				const currentTime = Math.floor(Date.now() / 1000);
				// Ensure we get the next interval, not the current one
				calculatedTimestamp = Math.floor(currentTime / intervalSeconds) * intervalSeconds + intervalSeconds;
			} else if (targetDate) {
				// For fixed countdowns, use cached timestamp or show placeholder
				calculatedTimestamp = componentSettings._calculated_timestamp || 0;
			}
			
			if (!targetDate && countdownType !== 'repeating') {
				componentElement = (
					<div 
						className="topbuddy-notification-bar-countdown"
						style={{ 
							cursor: 'pointer',
							padding: '8px 12px',
							background: 'rgba(255, 255, 255, 0.1)',
							borderRadius: '4px',
							fontSize: '13px',
							color: 'rgba(255, 255, 255, 0.5)',
						}}
					>
						<span className="dashicons dashicons-clock" style={{ fontSize: '16px', marginRight: '4px' }}></span>
						{__('Set target date', 'topbuddy')}
					</div>
				);
			} else {
				// Parse labels into arrays
				const pluralLabelsArray = labelsPlural.split(',').map(label => label.trim());
				const singularLabelsArray = labelsSingular.split(',').map(label => label.trim());
				
				// Render the same structure as PHP with data attributes for JS to use
				componentElement = (
					<div 
						className="topbuddy-notification-bar-countdown"
						data-theme={countdownTheme}
						data-target-timestamp={calculatedTimestamp}
						data-labels-plural={JSON.stringify(pluralLabelsArray)}
						data-labels-singular={JSON.stringify(singularLabelsArray)}
						data-on-complete={onComplete}
						data-message-text={messageText}
						data-target-date={targetDate}
						data-countdown-type={countdownType}
						data-repeat-interval={repeatInterval}
					style={{ cursor: 'pointer' }}
				>
					<span className="topbuddy-countdown-loading">
						{__('Loading...', 'topbuddy')}
					</span>
				</div>
			);
		}
		break;

		default:
			return null;
	}

	return (
		<SortablePreviewItem
			key={component.id}
			id={component.id}
			component={component}
			config={config}
			isExpanded={isExpanded}
			onToggleSettings={(e) => handleComponentClick(e, component.id)}
			onUpdateSettings={(newSettings) => handleUpdateSettings(component.id, newSettings)}
			onRemove={() => handleRemoveComponent(component.id)}
		>
			{componentElement}
		</SortablePreviewItem>
	);
	};

	return (
		<div className="topbuddy-preview-wrapper">
			<div className="topbuddy-preview-label">
				<strong>{__('Live Preview', 'topbuddy')}</strong>
				{/*<Button
					variant="link"
					onClick={() => setIsModalOpen(true)}
					style={{ marginLeft: '10px', textDecoration: 'none' }}
				>
					{__('Import a premade design', 'topbuddy')}
				</Button>*/}
			</div>
			<div className="topbuddy-notification-bar-preview">
			<div 
				ref={notificationBarRef}
				className="topbuddy-notification-bar"
				onClick={handleBarClick}
				style={{ cursor: 'pointer' }}
			>
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
					onDragCancel={handleDragCancel}
				>
					<SortableContext
						items={activeComponents.map((comp) => comp.id)}
						strategy={horizontalListSortingStrategy}
					>
						<div 
							className="topbuddy-notification-bar-container"
							style={{ cursor: 'pointer' }}
						>
							{activeComponents.length > 0 ? (
								activeComponents.map((component) => renderComponent(component))
							) : (
								<div className="topbuddy-preview-empty">
									{__('Add components to see preview', 'topbuddy')}
								</div>
							)}
						</div>
					</SortableContext>
				</DndContext>
				{settings.enableCloseButton !== false && (
					<span 
						className="topbuddy-notification-bar-close" 
						aria-label={__('Close Notification', 'topbuddy')}
						onClick={(e) => {
							e.stopPropagation();
							openSettingsTab();
						}}
						style={{ cursor: 'pointer' }}
						dangerouslySetInnerHTML={{ __html: icons.close || '×' }}
					/>
				)}
			</div>
			<div className="topbuddy-preview-add-component">
				<AddComponentButton
					availableComponents={getAvailableToAdd()}
					onAddComponent={handleAddComponent}
					isProActive={isProActive}
				/>
			</div>
			</div>
			{isModalOpen && (
				<Modal
					title={__('Template Library', 'topbuddy')}
					onRequestClose={() => setIsModalOpen(false)}
					className="topbuddy-template-library-modal"
				>
					<TemplateLibrary 
						onImport={(template) => {
							// Import functionality will be implemented later
							setIsModalOpen(false);
						}}
						onClose={() => setIsModalOpen(false)}
					/>
				</Modal>
			)}
		</div>
	);
};

export default NotificationBarPreview;
